import type {
  BudgetAssumption,
  BudgetSnapshot,
  CashPlanMonth,
  Client,
  ClientMetrics,
  CostLine,
  MonthlyCost,
  ToolItem,
  WorkActivity,
} from "../types";

export const currency = new Intl.NumberFormat("en-ZA", {
  style: "currency",
  currency: "ZAR",
  maximumFractionDigits: 2,
});

export const compactCurrency = new Intl.NumberFormat("en-ZA", {
  style: "currency",
  currency: "ZAR",
  maximumFractionDigits: 0,
});

export const shortDate = new Intl.DateTimeFormat("en-ZA", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

export function todayIso(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function monthIso(date = new Date()): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 7);
}

export function getNumericAssumption(
  assumptions: BudgetAssumption[],
  key: string,
  fallback = 0,
): number {
  const value = assumptions.find((item) => item.key === key)?.value_numeric;
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

export function getTextAssumption(
  assumptions: BudgetAssumption[],
  key: string,
  fallback = "",
): string {
  return assumptions.find((item) => item.key === key)?.value_text || fallback;
}

export function clientMetrics(
  client: Client,
  assumptions: BudgetAssumption[],
): ClientMetrics {
  if (client.status !== "Active") {
    return {
      averagePayments: 0,
      averageMonthlyIncome: 0,
      normalPayments: 0,
      normalMonthlyIncome: 0,
    };
  }

  const averageFactors = {
    Monthly: 1,
    "Every 2 weeks": getNumericAssumption(
      assumptions,
      "average_payments_every_2_weeks",
      26 / 12,
    ),
    Weekly: getNumericAssumption(
      assumptions,
      "average_payments_weekly",
      52 / 12,
    ),
    "Once-off": 0,
  };

  const normalFactors = {
    Monthly: 1,
    "Every 2 weeks": getNumericAssumption(
      assumptions,
      "normal_month_every_2_weeks",
      2,
    ),
    Weekly: getNumericAssumption(assumptions, "normal_month_weekly", 4),
    "Once-off": 0,
  };

  const averagePayments = averageFactors[client.frequency];
  const normalPayments = normalFactors[client.frequency];

  return {
    averagePayments,
    averageMonthlyIncome: averagePayments * client.amount_per_payment,
    normalPayments,
    normalMonthlyIncome: normalPayments * client.amount_per_payment,
  };
}

export function costLines(
  clients: Client[],
  costs: MonthlyCost[],
  assumptions: BudgetAssumption[],
): CostLine[] {
  const averageVisits = clients.reduce(
    (sum, client) => sum + clientMetrics(client, assumptions).averagePayments,
    0,
  );
  const litresPerVisit = getNumericAssumption(
    assumptions,
    "weed_cutter_petrol_per_visit",
    0.5,
  );
  const petrolPrice = getNumericAssumption(
    assumptions,
    "inland_95_petrol_price",
    26.92,
  );
  const oilMix = getNumericAssumption(
    assumptions,
    "two_stroke_oil_mix_ml_per_litre",
    20,
  );
  const oilPackSize = getNumericAssumption(
    assumptions,
    "two_stroke_oil_pack_size_ml",
    500,
  );
  const oilPackPrice = getNumericAssumption(
    assumptions,
    "two_stroke_oil_pack_price",
    120,
  );

  return costs
    .filter((item) => item.active)
    .map((item) => {
      let calculatedQuantity = item.quantity_per_month;
      let calculatedUnitCost = item.unit_cost;

      if (item.expense === "Weed cutter petrol") {
        calculatedQuantity = averageVisits * litresPerVisit;
        calculatedUnitCost = petrolPrice;
      }

      if (item.expense === "2-stroke oil") {
        calculatedQuantity = averageVisits * litresPerVisit * oilMix;
        calculatedUnitCost = oilPackSize > 0 ? oilPackPrice / oilPackSize : 0;
      }

      return {
        ...item,
        calculatedQuantity,
        calculatedUnitCost,
        monthlyCost: calculatedQuantity * calculatedUnitCost,
      };
    });
}

export function toolBudgetNeeded(tool: ToolItem): number {
  return tool.included && !tool.already_owned
    ? tool.quantity * tool.estimated_unit_cost
    : 0;
}

export function toolMonthlyReserve(tool: ToolItem): number {
  return tool.included && tool.useful_life_months > 0
    ? (tool.quantity * tool.estimated_unit_cost) / tool.useful_life_months
    : 0;
}

export function budgetSnapshot(
  clients: Client[],
  costs: MonthlyCost[],
  tools: ToolItem[],
  assumptions: BudgetAssumption[],
): BudgetSnapshot {
  const clientRows = clients.map((client) => clientMetrics(client, assumptions));
  const averageRecurringIncome = clientRows.reduce(
    (sum, row) => sum + row.averageMonthlyIncome,
    0,
  );
  const normalMonthIncome = clientRows.reduce(
    (sum, row) => sum + row.normalMonthlyIncome,
    0,
  );
  const averageVisits = clientRows.reduce(
    (sum, row) => sum + row.averagePayments,
    0,
  );
  const lines = costLines(clients, costs, assumptions);
  const cashOperatingCosts = lines
    .filter((line) => line.treatment === "Business")
    .reduce((sum, line) => sum + line.monthlyCost, 0);
  const personalWithdrawals = lines
    .filter((line) => line.treatment === "Personal withdrawal")
    .reduce((sum, line) => sum + line.monthlyCost, 0);
  const contingencyRate = getNumericAssumption(
    assumptions,
    "operating_contingency_rate",
    0.1,
  );
  const operatingContingency = cashOperatingCosts * contingencyRate;
  const equipmentReserve = tools.reduce(
    (sum, tool) => sum + toolMonthlyReserve(tool),
    0,
  );
  const businessAllocation =
    cashOperatingCosts + operatingContingency + equipmentReserve;
  const spendableMonthlyCash =
    averageRecurringIncome - businessAllocation - personalWithdrawals;
  const starterToolsToBuy = tools.reduce(
    (sum, tool) => sum + toolBudgetNeeded(tool),
    0,
  );

  return {
    averageRecurringIncome,
    normalMonthIncome,
    averageVisits,
    cashOperatingCosts,
    operatingContingency,
    equipmentReserve,
    businessAllocation,
    personalWithdrawals,
    spendableMonthlyCash,
    starterToolsToBuy,
    averageRevenuePerVisit:
      averageVisits > 0 ? averageRecurringIncome / averageVisits : 0,
  };
}

function addMonths(isoMonth: string, offset: number): Date {
  const [year, month] = isoMonth.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1 + offset, 1));
}

export function cashPlan(
  clients: Client[],
  costs: MonthlyCost[],
  tools: ToolItem[],
  assumptions: BudgetAssumption[],
): CashPlanMonth[] {
  const snapshot = budgetSnapshot(clients, costs, tools, assumptions);
  const start = getTextAssumption(
    assumptions,
    "plan_start_month",
    "2026-09-01",
  ).slice(0, 7);
  let openingCash = getNumericAssumption(
    assumptions,
    "opening_unallocated_cash",
    0,
  );
  let cumulativeReserve = 0;

  return Array.from({ length: 12 }, (_, index) => {
    const phase = `Month ${index + 1}`;
    const starterToolPurchases = tools
      .filter((tool) => tool.purchase_month === phase)
      .reduce((sum, tool) => sum + toolBudgetNeeded(tool), 0);
    const netMovement =
      snapshot.averageRecurringIncome -
      snapshot.cashOperatingCosts -
      snapshot.operatingContingency -
      snapshot.equipmentReserve -
      snapshot.personalWithdrawals -
      starterToolPurchases;
    const closingCash = openingCash + netMovement;
    cumulativeReserve += snapshot.equipmentReserve;
    const month = addMonths(start, index).toISOString().slice(0, 10);
    const row: CashPlanMonth = {
      month,
      phase,
      recurringIncome: snapshot.averageRecurringIncome,
      operatingCosts: snapshot.cashOperatingCosts,
      contingency: snapshot.operatingContingency,
      equipmentReserve: snapshot.equipmentReserve,
      personalWithdrawals: snapshot.personalWithdrawals,
      starterToolPurchases,
      netMovement,
      openingCash,
      closingCash,
      cumulativeReserve,
    };
    openingCash = closingCash;
    return row;
  });
}

export function minimumOpeningCash(plan: CashPlanMonth[]): number {
  const lowestClose = Math.min(...plan.map((row) => row.closingCash));
  return Math.max(0, -lowestClose);
}

export function durationBetween(start: string, end: string): number {
  if (!start || !end) return 0;
  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);
  let minutes = endHour * 60 + endMinute - (startHour * 60 + startMinute);
  if (minutes < 0) minutes += 24 * 60;
  return minutes;
}

export function activityCost(
  activity: WorkActivity,
  assumptions: BudgetAssumption[],
): number {
  const petrolPrice = getNumericAssumption(
    assumptions,
    "inland_95_petrol_price",
    26.92,
  );
  const oilPackSize = getNumericAssumption(
    assumptions,
    "two_stroke_oil_pack_size_ml",
    500,
  );
  const oilPackPrice = getNumericAssumption(
    assumptions,
    "two_stroke_oil_pack_price",
    120,
  );
  const oilPricePerMl = oilPackSize > 0 ? oilPackPrice / oilPackSize : 0;

  return (
    activity.petrol_litres * petrolPrice +
    activity.two_stroke_oil_ml * oilPricePerMl +
    activity.trimmer_line_cost +
    activity.refuse_bags_cost +
    activity.maintenance_cost +
    activity.materials_cost +
    activity.transport_cost +
    activity.phone_data_cost +
    activity.other_cost
  );
}

export function monthLabel(isoDate: string): string {
  return new Intl.DateTimeFormat("en-ZA", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${isoDate.slice(0, 10)}T00:00:00Z`));
}
